import { DomainError } from '@membrana/core';

import { TARIFF_DATASET_SYSTEM_KEY, DEFAULT_SAMPLES_PAGE_SIZE } from '../constants.js';
import { resolveMediaLibraryTraceId } from '../media-library-trace.js';
import type { IStorageBackend } from '../ports/storage-backend.js';
import type {
  DeleteByIdsOutcome,
  MoveBatchOutcome,
  BufferCleanupPlanOutcome,
  BufferCleanupPlanRequest,
  Collection,
  CollectionDetectorBatchRequest,
  CollectionDetectorBatchRunOutcome,
  LibraryChartListRequest,
  LibraryChartListRunOutcome,
  SessionDigestRequest,
  SessionDigestRunOutcome,
  LibraryDuplicatesRequest,
  LibraryDuplicatesRunOutcome,
  CollectionKind,
  MediaSample,
  MediaPluginState,
  NewSampleMeta,
  SampleLabel,
  SampleSource,
  StorageQuota,
  UpdateSampleLabelNotes,
  PaginatedSamples,
} from '../types.js';

type WithoutRunId<T> = T extends unknown ? Omit<T, 'runId'> : never;

export interface ServerStorageBackendConfig {
  baseUrl: string;
  deviceId: string;
  mediaToken: string;
}

interface ApiCollection {
  id: string;
  name: string;
  kind: string;
  createdAt: string;
  updatedAt: string;
  systemKey?: string;
  sampleCount?: number;
}

interface ApiPaginatedSamples {
  items: ApiSample[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ApiSample {
  id: string;
  collectionId: string;
  title: string;
  class: string;
  label: SampleLabel;
  source: string;
  durationSec: number;
  sampleRate: number;
  channels: 1 | 2;
  createdAt: string;
  storageRef: string;
  notes?: string;
  sizeBytes: number;
}

interface ApiQuotaResponse {
  userStorage: { usedBytes: number; limitBytes: number; backend: 'server' };
  buffer: { usedBytes: number; limitBytes: number; backend: 'server' };
  dataset: { catalogId: string; sampleCount: number };
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

function mapCollectionKind(kind: string): CollectionKind {
  if (kind === 'buffer' || kind === 'user' || kind === 'system') {
    return kind;
  }
  return 'user';
}

function mapCollection(dto: ApiCollection): Collection {
  return {
    id: dto.id,
    name: dto.name,
    kind: mapCollectionKind(dto.kind),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    systemKey: dto.systemKey === TARIFF_DATASET_SYSTEM_KEY ? TARIFF_DATASET_SYSTEM_KEY : undefined,
    sampleCount: dto.sampleCount,
  };
}

function mapSampleSource(source: string): SampleSource {
  const allowed: SampleSource[] = [
    'mic-recording',
    'disk-import',
    'synthetic',
    'move',
    'copy',
    'catalog',
  ];
  return allowed.includes(source as SampleSource) ? (source as SampleSource) : 'disk-import';
}

function mapSampleLabel(label: string): SampleLabel {
  if (label === 'not_drone' || label === 'not-drone') return 'not-drone';
  if (label === 'drone') return 'drone';
  return 'unlabeled';
}

function mapSample(dto: ApiSample): MediaSample {
  return {
    id: dto.id,
    collectionId: dto.collectionId,
    title: dto.title,
    class: dto.class,
    label: mapSampleLabel(dto.label),
    source: mapSampleSource(dto.source),
    durationSec: dto.durationSec,
    sampleRate: dto.sampleRate,
    channels: dto.channels === 2 ? 2 : 1,
    createdAt: dto.createdAt,
    storageRef: dto.storageRef,
    notes: dto.notes,
    sizeBytes: dto.sizeBytes,
  };
}

function guessUploadFilename(blob: Blob, title: string): string {
  const base = title.trim() || 'sample';
  const type = blob.type || 'audio/wav';
  if (type.includes('webm')) return `${base}.webm`;
  if (type.includes('mp4')) return `${base}.mp4`;
  if (type.includes('mpeg') || type.includes('mp3')) return `${base}.mp3`;
  if (type.includes('ogg')) return `${base}.ogg`;
  if (type.includes('flac')) return `${base}.flac`;
  return `${base}.wav`;
}

async function readResponseText(res: Response): Promise<string> {
  return res.text();
}

function isHtmlPayload(text: string): boolean {
  const trimmed = text.trimStart().toLowerCase();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
}

function parseJsonText<T>(text: string, baseUrl: string): T {
  if (isHtmlPayload(text)) {
    throw new DomainError(
      `Media API returned HTML instead of JSON (check baseUrl: ${baseUrl})`,
      'REQUEST_FAILED',
    );
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new DomainError(
      `Media API returned invalid JSON (check baseUrl: ${baseUrl})`,
      'REQUEST_FAILED',
    );
  }
}

async function parseApiError(res: Response, baseUrl: string): Promise<string> {
  try {
    const text = await readResponseText(res);
    const body = parseJsonText<{ message?: string | string[] }>(text, baseUrl);
    if (Array.isArray(body.message)) return body.message.join(', ');
    if (typeof body.message === 'string') return body.message;
  } catch (err) {
    if (err instanceof DomainError) return err.message;
  }
  return res.statusText || 'Request failed';
}

function throwForStatus(res: Response, message: string): never {
  if (res.status === 403) throw new DomainError(message, 'FORBIDDEN');
  if (res.status === 404) throw new DomainError(message, 'NOT_FOUND');
  /*
    413 — только настоящему «тело слишком большое» (заседание buffer-full-stop M2, #2309).
    Раньше здесь стояло `QUOTA_EXCEEDED`, и ночью 05→06.09 сервер одиннадцать часов отвечал
    413 на полный буфер, а клиент маппил его в квоту без единого потребителя. Квота теперь
    приходит доменным отказом `200 {ok:false, reason}` — см. `parseSampleRefusal`.
  */
  if (res.status === 413) throw new DomainError(message, 'PAYLOAD_TOO_LARGE');
  throw new DomainError(message, 'REQUEST_FAILED');
}

/**
 * Оси квоты в доменном отказе (те же, что в `GET /quota`). Оба поля — целые байты.
 */
export interface SampleRefusalAxis {
  readonly usedBytes: number;
  readonly limitBytes: number;
}

/**
 * Доменный отказ сервера записей на POST пробы (конвенция кабинета 12.08, M2 #2309):
 * HTTP 200 + `{ ok:false, reason, … }`. Бэкенд — транспорт: словаря причин он НЕ знает и
 * не проверяет; литерал `reason` судит носитель удержания на приборе (`DeviceOverflowHold`).
 * Поля эпизода (`overflowId`, `overflowAt`, `overflowPolicy`) и оси квоты пробрасываются как
 * есть, если сервер их дал; `raw` — полное тело для окна оператора (M5 строит окно из ответа).
 */
export interface SampleRefusal {
  readonly reason: string;
  readonly overflowId: string | null;
  readonly overflowAt: string | null;
  readonly overflowPolicy: string | null;
  readonly buffer: SampleRefusalAxis | null;
  readonly userStorage: SampleRefusalAxis | null;
  readonly raw: Readonly<Record<string, unknown>>;
}

export type SampleRefusalListener = (refusal: SampleRefusal) => void;

/** Шлюз отправки: `true` — отправка новых проб удержана, POST не делается (M3: 0 ретраев). */
export type SampleUploadGate = () => boolean;

const sampleRefusalListeners = new Set<SampleRefusalListener>();

let sampleUploadGate: SampleUploadGate | null = null;

function readAxis(value: unknown): SampleRefusalAxis | null {
  if (typeof value !== 'object' || value === null) return null;
  const rec = value as Record<string, unknown>;
  const usedBytes = Number(rec.usedBytes);
  const limitBytes = Number(rec.limitBytes);
  if (!Number.isFinite(usedBytes) || !Number.isFinite(limitBytes)) return null;
  return { usedBytes, limitBytes };
}

function readOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Разбор тела ответа на POST пробы: доменный отказ или null (обычный ответ с пробой).
 * Отказ = объект с `ok === false` и строковым `reason`; всё остальное — не отказ.
 */
export function parseSampleRefusal(body: unknown): SampleRefusal | null {
  if (typeof body !== 'object' || body === null) return null;
  const rec = body as Record<string, unknown>;
  if (rec.ok !== false) return null;
  const reason = readOptionalString(rec.reason);
  if (reason === null) return null;
  return {
    reason,
    overflowId: readOptionalString(rec.overflowId),
    overflowAt: readOptionalString(rec.overflowAt),
    overflowPolicy: readOptionalString(rec.overflowPolicy),
    buffer: readAxis(rec.buffer),
    userStorage: readAxis(rec.userStorage),
    raw: rec,
  };
}

function announceSampleRefusal(refusal: SampleRefusal): void {
  for (const listener of sampleRefusalListeners) {
    try {
      listener(refusal);
    } catch {
      /* слушатель не должен ронять путь отправки */
    }
  }
}

/** HTTP backend for `background-media` (paired web client). */
export class ServerStorageBackend implements IStorageBackend {
  private readonly baseUrl: string;

  private readonly deviceId: string;

  private readonly mediaToken: string;

  private serverReachable = true;

  constructor(config: ServerStorageBackendConfig) {
    this.baseUrl = normalizeBaseUrl(config.baseUrl);
    this.deviceId = config.deviceId;
    this.mediaToken = config.mediaToken;
  }

  /**
   * Подписка на доменные отказы POST пробы (#2309). Реестр модульный, как
   * `setMediaLibraryTraceHook`: бэкенд конструирует не прибор, а резолвер соединения, поэтому
   * колбэк в конфиге до носителя удержания не доехал бы. Статический метод — чтобы не трогать
   * барель пакета (общий файл коворка).
   */
  static onSampleRefusal(listener: SampleRefusalListener): () => void {
    sampleRefusalListeners.add(listener);
    return () => {
      sampleRefusalListeners.delete(listener);
    };
  }

  /**
   * Шлюз отправки новых проб (#2309, M3 DoD 1): при `gate() === true` `putSample` не делает
   * `fetch` вовсе — ноль исходящих POST в закрытую дверь, никаких ретраев. Единственный
   * законный источник `true` — `DeviceOverflowHold.isHeld()`; второй машины здесь нет.
   */
  static setSampleUploadGate(gate: SampleUploadGate | null): void {
    sampleUploadGate = gate;
  }

  /** Тесты: очистить реестр слушателей и шлюз. */
  static resetSampleRefusalWiringForTests(): void {
    sampleRefusalListeners.clear();
    sampleUploadGate = null;
  }

  private deviceUrl(path: string): string {
    return `${this.baseUrl}/v1/devices/${this.deviceId}${path}`;
  }

  private authHeaders(extra?: HeadersInit): Headers {
    const headers = new Headers(extra);
    headers.set('X-Membrana-Token', this.mediaToken);
    headers.set('X-Membrana-Device-Id', this.deviceId);
    const traceId = resolveMediaLibraryTraceId();
    if (traceId !== null && traceId.length > 0) {
      headers.set('X-Membrana-Trace-Id', traceId);
    }
    return headers;
  }

  private async requestJson<T>(path: string, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.deviceUrl(path), {
        ...init,
        headers: this.authHeaders(init?.headers),
      });
    } catch {
      this.serverReachable = false;
      throw new DomainError('Media-server network error', 'REQUEST_FAILED');
    }
    if (!res.ok) {
      this.serverReachable = res.status !== 401 && res.status !== 403;
      throwForStatus(res, await parseApiError(res, this.baseUrl));
    }
    this.serverReachable = true;
    const text = await readResponseText(res);
    return parseJsonText<T>(text, this.baseUrl);
  }

  /**
   * Сырой ответ `GET /quota` — как отдал сервер записей, без проекции в `StorageQuota`
   * (адаптер BC-1 интеграции `cowork-buffer-full-stop`). Нужен читателю эффективной политики
   * переполнения (блок B): поле `bufferPolicy` едет в корне `/quota`, а `getQuota()` его
   * отбрасывает. Бросает при недоступности сервера — для читателя это «дыра синка» (→ `stop`).
   * Частоту чтения задаёт вызывающий: сам бэкенд в сеть по расписанию не ходит.
   */
  async getQuotaRaw(): Promise<unknown> {
    return this.requestJson<unknown>('/quota');
  }

  async getQuota(): Promise<StorageQuota> {
    try {
      const data = await this.requestJson<ApiQuotaResponse>('/quota');
      return {
        usedBytes: data.userStorage.usedBytes,
        limitBytes: data.userStorage.limitBytes,
        backend: 'server',
        serverReachable: this.serverReachable,
        bufferUsedBytes: data.buffer.usedBytes,
        bufferLimitBytes: data.buffer.limitBytes,
        ...(data.dataset
          ? { dataset: { catalogId: data.dataset.catalogId, sampleCount: data.dataset.sampleCount } }
          : {}),
      };
    } catch {
      return {
        usedBytes: 0,
        limitBytes: 0,
        backend: 'server',
        serverReachable: false,
        bufferUsedBytes: 0,
        bufferLimitBytes: 0,
      };
    }
  }

  /**
   * Отбор чарт-листа по текущему набору (#2110): боевой вход media
   * POST /collections/:id/plugins/:pluginId/request. Выборку считает витрина на сервере —
   * здесь только заказ и разбор ответа; result приходит тем же ответом (канал c5c).
   */
  async requestSessionDigest(
    collectionId: string,
    req: SessionDigestRequest,
  ): Promise<SessionDigestRunOutcome> {
    const row = await this.requestJson<{ runId: string; result?: Omit<SessionDigestRunOutcome, 'runId'> }>(
      `/collections/${encodeURIComponent(collectionId)}/plugins/membrana.report.session-digest/request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(req.from ? { from: req.from } : {}),
          ...(req.to ? { to: req.to } : {}),
        }),
      },
    );
    if (!row.result) throw new Error('media не вернул результат свода сеанса — прогон без исхода');
    return { runId: row.runId, ...row.result };
  }

  async requestLibraryDuplicates(
    collectionId: string,
    req: LibraryDuplicatesRequest,
  ): Promise<LibraryDuplicatesRunOutcome> {
    const row = await this.requestJson<{ runId: string; result?: Omit<LibraryDuplicatesRunOutcome, 'runId'> }>(
      `/collections/${encodeURIComponent(collectionId)}/plugins/membrana.showcase.library-duplicates/request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(req.from ? { from: req.from } : {}),
          ...(req.to ? { to: req.to } : {}),
        }),
      },
    );
    if (!row.result) throw new Error('media не вернул результат поиска дублей — прогон без исхода');
    return { runId: row.runId, ...row.result };
  }

  async requestLibraryChartList(
    collectionId: string,
    req: LibraryChartListRequest,
  ): Promise<LibraryChartListRunOutcome> {
    const row = await this.requestJson<{ runId: string; result?: Omit<LibraryChartListRunOutcome, 'runId'> }>(
      `/collections/${encodeURIComponent(collectionId)}/plugins/membrana.showcase.library-chart-list/request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          volume: req.volume,
          criterion: req.criterion,
          ...(req.from ? { from: req.from } : {}),
          ...(req.to ? { to: req.to } : {}),
        }),
      },
    );
    if (!row.result) {
      // Прогон был, а результата нет — это поломка канала, не пустая выборка: молча показать
      // ноль строк значило бы выдать сбой за «в наборе ничего не нашлось».
      throw new Error('Витрина отбора не вернула результат прогона (канал result пуст)');
    }
    return { runId: row.runId, ...row.result };
  }

  async requestCollectionDetectorBatch(
    collectionId: string,
    req: CollectionDetectorBatchRequest = {},
  ): Promise<CollectionDetectorBatchRunOutcome> {
    const row = await this.requestJson<{
      runId: string;
      result?: WithoutRunId<CollectionDetectorBatchRunOutcome>;
    }>(
      `/collections/${encodeURIComponent(collectionId)}/plugins/membrana.report.detector-batch/request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...(req.sampleIds ? { sampleIds: req.sampleIds } : {}) }),
      },
    );
    if (!row.result) {
      throw new Error('media не вернул результат batch-прогона детекторов');
    }
    return { runId: row.runId, ...row.result };
  }

  /**
   * План уборки буфера (#2204). Отдельная ручка, а не `plugins/…/request`: прогон витрины
   * отвечает результатом по runId и живёт в контракте первой волны, а уборке нужны два шага
   * с человеком посередине — показать и дождаться слова.
   */
  async planBufferCleanup(
    collectionId: string,
    req: BufferCleanupPlanRequest,
  ): Promise<BufferCleanupPlanOutcome> {
    return this.requestJson<BufferCleanupPlanOutcome>(
      `/collections/${encodeURIComponent(collectionId)}/buffer-cleanup/plan`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ principle: req.principle, volume: req.volume }),
      },
    );
  }

  /**
   * Уборка буфера (#2204): уходят РОВНО перечисленные. Пустой список сюда не отправляется —
   * его отбивает и клиент, и сервер: удаление без показанного списка невозможно по контракту.
   */
  async deleteSamplesByIds(
    collectionId: string,
    sampleIds: readonly string[],
  ): Promise<DeleteByIdsOutcome> {
    if (sampleIds.length === 0) {
      throw new Error('Уборка без списка невозможна: сперва план, потом слово человека');
    }
    return this.requestJson<DeleteByIdsOutcome>(
      `/collections/${encodeURIComponent(collectionId)}/buffer-cleanup/execute`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sampleIds: [...sampleIds] }),
      },
    );
  }

  async listCollectionPlugins(collectionId: string): Promise<readonly MediaPluginState[]> {
    const row = await this.requestJson<{ plugins: readonly MediaPluginState[] }>(
      `/collections/${encodeURIComponent(collectionId)}/plugins`,
    );
    return row.plugins;
  }

  async setCollectionPluginEnabled(
    collectionId: string,
    pluginId: string,
    enabled: boolean,
  ): Promise<void> {
    await this.requestJson<{ ok: true }>(
      `/collections/${encodeURIComponent(collectionId)}/plugins/${encodeURIComponent(pluginId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      },
    );
  }

  async ensureReservedCollections(): Promise<void> {
    await this.requestJson<ApiCollection[]>('/collections/ensure-reserved', { method: 'POST' });
  }

  async listCollections(): Promise<Collection[]> {
    const rows = await this.requestJson<ApiCollection[]>('/collections');
    return rows.map(mapCollection);
  }

  async createCollection(name: string): Promise<Collection> {
    const row = await this.requestJson<ApiCollection>('/collections', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    return mapCollection(row);
  }

  async deleteCollection(id: string): Promise<void> {
    const res = await fetch(this.deviceUrl(`/collections/${encodeURIComponent(id)}`), {
      method: 'DELETE',
      headers: this.authHeaders(),
    });
    if (!res.ok) {
      throwForStatus(res, await parseApiError(res, this.baseUrl));
    }
  }

  async listSamplesPage(
    collectionId: string,
    page = 1,
    limit = DEFAULT_SAMPLES_PAGE_SIZE,
  ): Promise<PaginatedSamples> {
    const data = await this.requestJson<ApiPaginatedSamples>(
      `/collections/${encodeURIComponent(collectionId)}/samples?page=${page}&limit=${limit}`,
    );
    return {
      items: data.items.map(mapSample),
      page: data.page,
      limit: data.limit,
      total: data.total,
      totalPages: data.totalPages,
    };
  }

  async listSamples(collectionId: string): Promise<MediaSample[]> {
    const first = await this.listSamplesPage(collectionId, 1, DEFAULT_SAMPLES_PAGE_SIZE);
    const rows = [...first.items];
    for (let page = 2; page <= first.totalPages; page += 1) {
      const next = await this.listSamplesPage(collectionId, page, first.limit);
      rows.push(...next.items);
    }
    return rows;
  }

  async putSample(collectionId: string, blob: Blob, meta: NewSampleMeta): Promise<MediaSample> {
    if (sampleUploadGate?.() === true) {
      throw new DomainError(
        'Отправка проб удержана: буфер полон — новые пробы не отправляются до сброса удержания',
        'UPLOAD_HELD',
      );
    }
    const form = new FormData();
    form.append('file', blob, guessUploadFilename(blob, meta.title));
    form.append(
      'meta',
      JSON.stringify({
        title: meta.title,
        class: meta.class,
        label: meta.label,
        source: meta.source,
        durationSec: meta.durationSec,
        sampleRate: meta.sampleRate,
        channels: meta.channels ?? 1,
        notes: meta.notes,
      }),
    );
    const res = await fetch(
      this.deviceUrl(`/collections/${encodeURIComponent(collectionId)}/samples`),
      {
        method: 'POST',
        headers: this.authHeaders(),
        body: form,
      },
    );
    if (!res.ok) {
      throwForStatus(res, await parseApiError(res, this.baseUrl));
    }
    const text = await readResponseText(res);
    const body = parseJsonText<unknown>(text, this.baseUrl);
    const refusal = parseSampleRefusal(body);
    if (refusal !== null) {
      announceSampleRefusal(refusal);
      throw new DomainError(
        `Сервер записей отказал в приёме пробы: ${refusal.reason}`,
        'SAMPLE_REFUSED',
        refusal,
      );
    }
    return mapSample(body as ApiSample);
  }

  async removeSample(sampleId: string): Promise<void> {
    const res = await fetch(this.deviceUrl(`/samples/${encodeURIComponent(sampleId)}`), {
      method: 'DELETE',
      headers: this.authHeaders(),
    });
    if (!res.ok) {
      throwForStatus(res, await parseApiError(res, this.baseUrl));
    }
  }

  /**
   * Массовый вывоз из буфера в набор. ОДНА дверь на тысячу проб, а не тысяча вызовов
   * `moveSample`: буфер прибора `9e86ec85` держит 1057 проб, и отказ на пятисотой оставил бы
   * человека с половиной вывоза и без числа, сколько уехало.
   *
   * Пустой список сюда не отправляется: переносить без показанного человеку списка нечего —
   * это же правило, что у удаления по списку.
   *
   * Ответ 200 с непустым `stayed` — ШТАТНЫЙ ИСХОД (частичный перенос), поэтому исключения
   * здесь нет: `requestJson` бросает только на настоящих ошибках транспорта и на отказах
   * целиком (нет прибора, нет набора, цель — сам буфер, список длиннее `maxBatch`).
   */
  async moveSamplesBatch(
    sampleIds: readonly string[],
    toCollectionId: string,
    options: { readonly dryRun?: boolean } = {},
  ): Promise<MoveBatchOutcome> {
    if (sampleIds.length === 0) {
      throw new Error('Перенос без списка невозможен: сперва план, потом слово человека');
    }
    return this.requestJson<MoveBatchOutcome>('/samples/move-batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sampleIds: [...sampleIds],
        toCollectionId,
        ...(options.dryRun === true ? { dryRun: true } : {}),
      }),
    });
  }

  async moveSample(sampleId: string, toCollectionId: string): Promise<MediaSample> {
    const row = await this.requestJson<ApiSample>(
      `/samples/${encodeURIComponent(sampleId)}/move`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toCollectionId }),
      },
    );
    return mapSample(row);
  }

  async updateSampleLabelNotes(
    sampleId: string,
    patch: UpdateSampleLabelNotes,
  ): Promise<MediaSample> {
    const body: { label?: string; notes?: string | null } = {};
    if (patch.label !== undefined) body.label = patch.label;
    if (patch.notes !== undefined) body.notes = patch.notes;
    const row = await this.requestJson<ApiSample>(
      `/samples/${encodeURIComponent(sampleId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return mapSample(row);
  }

  async readBlob(sampleId: string): Promise<Blob> {
    const res = await fetch(this.deviceUrl(`/samples/${encodeURIComponent(sampleId)}/blob`), {
      headers: this.authHeaders(),
    });
    if (!res.ok) {
      throwForStatus(res, await parseApiError(res, this.baseUrl));
    }
    return await res.blob();
  }
}

export function createServerStorageBackend(
  config: ServerStorageBackendConfig,
): ServerStorageBackend {
  return new ServerStorageBackend(config);
}
